import { CircleButton, Switch, Tooltip, Typography, getCSSVar } from "@tosize/ui";
import { Label, type Slider as SliderPrimitive } from "radix-ui";
import { useCallback } from "react";
import { FormattedMessage, useIntl } from "react-intl";
import { useCabinetStore } from "../../context/StoreContext";
import { useCanvasStore } from "../../hooks/useCanvasStore";
import { Slider } from "../Slider";
import TitleBlock from "../TitleBlock";
import { createHorizontals, createVerticals, isLongPartsBuildMethod } from "../renderer/data";
import { BACKGROUND_THICKNESS, createBackground } from "../renderer/data/Background";
import { getMinSize, updateCompartment } from "../renderer/data/Compartment";
import { FEET_HEIGHT } from "../renderer/parts/Horizontal";

// TODO: Fix shelves when resizing
export default function Dimensions() {
	const intl = useIntl();
	const {
		state,
		setStore,
		getOuterConstraints,
		mergeCompartmentsOnResize,
		splitVerticallyRecursivelyOnResize,
		splitHorizontallyRecursivelyOnResize,
		api,
	} = useCabinetStore();

	const constraints = getOuterConstraints(state.material, state.thickness);

	const setWidth = (value: number) => {
		const compartments = new Map(state.compartments);

		const rightCompartments = Array.from(compartments.values()).filter(
			(compartment) => compartment.x + compartment.width === state.width - state.thickness,
		);
		const widthDiff = value - state.width;

		// Update most right compartments
		for (const compartment of rightCompartments) {
			compartments.set(
				compartment.id,
				updateCompartment(
					{ ...compartment, width: compartment.width + widthDiff },
					state.thickness,
				),
			);
		}

		// Merge and delete compartments
		for (const compartment of compartments.values()) {
			if (compartment.x > value) {
				compartments.delete(compartment.id);
			}
			if (compartment.width < getMinSize(compartment, "left", state, api)) {
				mergeCompartmentsOnResize(compartments, compartment, "left", state);
			}
		}

		// Split compartments that are too big
		splitVerticallyRecursivelyOnResize(compartments, "left", state);

		// Update state
		setStore((state) => ({
			...state,
			width: value,
			compartments: compartments,
			horizontals: createHorizontals(compartments, state.thickness, state.material, api),
			verticals: createVerticals(compartments, state.thickness, value),
		}));
	};

	const setHeight = (value: number) => {
		const compartments = new Map(state.compartments);
		const topCompartments = Array.from(compartments.values()).filter(
			(compartment) => compartment.y + compartment.height === state.height - state.thickness,
		);
		const heightDiff = value - state.height;

		// Update most upper compartments
		for (const compartment of topCompartments) {
			// Future: update updateCompartment() too split and merge everything at once?
			compartments.set(
				compartment.id,
				updateCompartment(
					{ ...compartment, height: compartment.height + heightDiff },
					state.thickness,
				),
			);
		}

		// Merge and delete compartments
		for (const compartment of compartments.values()) {
			if (compartment.y > value) {
				compartments.delete(compartment.id);
			}
			if (compartment.height < getMinSize(compartment, "bottom", state, api)) {
				mergeCompartmentsOnResize(compartments, compartment, "bottom", state);
			}
		}

		// Split compartments that are too big
		splitHorizontallyRecursivelyOnResize(compartments, state);

		// Update state
		setStore((state) => ({
			...state,
			height: value,
			compartments,
			horizontals: createHorizontals(compartments, state.thickness, state.material, api),
			verticals: createVerticals(compartments, state.thickness, state.width),
		}));
	};

	const setDepth = (value: number) => {
		const compartments = new Map(state.compartments);

		for (const compartment of compartments.values()) {
			compartments.set(
				compartment.id,
				updateCompartment(
					{
						...compartment,
						depth: value,
						background: compartment.background
							? createBackground({
									...compartment,
									background: {
										...compartment.background,
										z: Math.min(compartment.background.z, value - BACKGROUND_THICKNESS),
									},
								})
							: undefined,
					},
					state.thickness,
				),
			);
		}

		return setStore((state) => ({
			...state,
			depth: value,
			compartments,
			horizontals: createHorizontals(compartments, state.thickness, state.material, api),
			verticals: createVerticals(compartments, state.thickness, state.width),
		}));
	};

	const toggleFeet = () => {
		return setStore((state) => {
			const newHeight = isLongPartsBuildMethod(state.thickness)
				? state.hasFeet
					? state.height + FEET_HEIGHT * 1000
					: state.height - FEET_HEIGHT * 1000
				: state.height;

			return {
				...state,
				hasFeet: !state.hasFeet,
				height: newHeight,
			};
		});
	};

	const adjustFeetTip = (
		<>
			<Typography>
				{intl.formatMessage({
					defaultMessage:
						"Het type stelvoetje hangt af van de dikte van de kast. Bij kasten van 18 mm dik worden de stelvoetjes onder de onderste ligger geplaatst, waardoor de kast 5 mm van de grond staat. Bij liggers dikker dan 28 mm worden de stelvoetjes in de onderste ligger gefreesd, zodat ze niet zichtbaar zijn.",
				})}
			</Typography>
			<br />
			<Typography>
				{intl.formatMessage({
					defaultMessage:
						"Met stelvoetjes kun je de kast van 0 tot 8 mm waterpas stellen. We raden altijd aan om stelvoetjes te gebruiken, want geen enkele vloer is perfect vlak.",
				})}
			</Typography>
		</>
	);

	return (
		<div className="flex flex-col gap-y-1.5">
			<TitleBlock title={intl.formatMessage({ defaultMessage: "Afmetingen (buitenmaten)" })} />

			<div className="grid grid-cols-4 gap-x-4 gap-y-3">
				<Label.Root htmlFor="width" className="font-bold text-sm">
					<FormattedMessage defaultMessage="Breedte" />
				</Label.Root>
				<DimensionSlider
					name="width"
					step={1}
					min={constraints.width.min / 10}
					max={constraints.width.max / 10}
					value={[Math.round(state.width / 10)]}
					onValueChange={([value]) => {
						if (value) {
							setWidth(value * 10);
						}
					}}
				/>

				<Label.Root htmlFor="height" className="font-bold text-sm">
					<FormattedMessage defaultMessage="Hoogte" />
				</Label.Root>
				<DimensionSlider
					name="height"
					step={1}
					min={constraints.height.min / 10}
					max={constraints.height.max / 10}
					value={[Math.round(state.height / 10)]}
					onValueChange={([value]) => {
						if (value) {
							setHeight(value * 10);
						}
					}}
				/>

				<Label.Root htmlFor="depth" className="font-bold text-sm">
					<FormattedMessage defaultMessage="Diepte" />
				</Label.Root>
				<DimensionSlider
					name="depth"
					step={1}
					min={constraints.depth.min / 10}
					max={constraints.depth.max / 10}
					value={[Math.round(state.depth / 10)]}
					onValueChange={([value]) => {
						if (value) {
							setDepth(value * 10);
						}
					}}
				/>
			</div>

			<Switch
				labelPosition="left"
				className="mt-2 justify-between font-bold text-sm"
				id="include-feet"
				checked={state.hasFeet}
				onCheckedChange={() => toggleFeet()}
			>
				<FormattedMessage defaultMessage="Stelvoetjes" />
				<Tooltip content={adjustFeetTip} color="shades-light">
					<CircleButton component="button" className="ml-2 text-sm" />
				</Tooltip>
			</Switch>
		</div>
	);
}

function DimensionSlider(props: SliderPrimitive.SliderProps) {
	const { setLayoutMode, setShowDimensions, setAltControls } = useCanvasStore();

	// Disable some functions when dimensions are changed for better performance
	const setCanvas = useCallback(() => {
		setLayoutMode(null);
		setShowDimensions(false);
		setAltControls(false);
	}, [setLayoutMode, setShowDimensions, setAltControls]);

	const scrollToCanvas = useCallback(() => {
		const element = document.querySelector("#start-ordering");
		const top = (element?.getBoundingClientRect().top ?? 0) + window.scrollY;
		const headerHeight = getCSSVar("--header-y");

		window.scrollTo({ top: top - headerHeight, behavior: "smooth" });
	}, []);

	const onValueDown = useCallback(() => {
		setCanvas();

		const currentValue = props.value?.at(0) ?? 0;
		const constraint = props.min ?? 0;
		const newValue = currentValue - 1;

		if (constraint < currentValue) {
			props.onValueChange?.([newValue]);
		}
	}, [setCanvas, props.min, props.onValueChange, props.value]);

	const onValueUp = useCallback(() => {
		setCanvas();

		const currentValue = props.value?.at(0) ?? 0;
		const constraint = props.max ?? Number.POSITIVE_INFINITY;
		const newValue = currentValue + 1;

		if (constraint > currentValue) {
			props.onValueChange?.([newValue]);
		}
	}, [setCanvas, props.max, props.onValueChange, props.value]);

	return (
		<Slider
			{...props}
			className="col-span-3"
			onValueDown={onValueDown}
			onValueUp={onValueUp}
			onValueCommit={scrollToCanvas}
			onPointerDown={setCanvas}
		/>
	);
}
